/* eslint-disable no-console */
import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import styles from "./PropuestasExternas.module.scss";

const MONTHS = [
  { value: "01", label: "ENE" },
  { value: "02", label: "FEB" },
  { value: "03", label: "MAR" },
  { value: "04", label: "ABR" },
  { value: "05", label: "MAY" },
  { value: "06", label: "JUN" },
  { value: "07", label: "JUL" },
  { value: "08", label: "AGO" },
  { value: "09", label: "SEP" },
  { value: "10", label: "OCT" },
  { value: "11", label: "NOV" },
  { value: "12", label: "DIC" },
];

const CURRENT_YEAR = new Date().getFullYear();

const MonthYearPicker = ({ month, year, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(Number(year) || CURRENT_YEAR);
  const ref = useRef(null);

  useEffect(() => {
    setDisplayYear(Number(year) || CURRENT_YEAR);
  }, [year]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const monthLabel = MONTHS.find((m) => m.value === month)?.label || "---";

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <label style={{ fontSize: "0.82rem", fontWeight: 500, marginBottom: 4, display: "block", color: "#333" }}>
        {label}
      </label>
      <button
        type="button"
        className="form-control"
        style={{ fontSize: "0.85rem", cursor: "pointer", textAlign: "left" }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {monthLabel} {year}
      </button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1060,
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: 10,
            width: 220,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <button
              type="button"
              style={{ border: "none", background: "transparent", fontSize: "1.2rem", cursor: "pointer" }}
              onClick={() => setDisplayYear((y) => Math.max(CURRENT_YEAR, y - 1))}
              disabled={displayYear <= CURRENT_YEAR}
            >
              &lsaquo;
            </button>
            <span style={{ fontWeight: 600 }}>{displayYear}</span>
            <button
              type="button"
              style={{ border: "none", background: "transparent", fontSize: "1.2rem", cursor: "pointer" }}
              onClick={() => setDisplayYear((y) => Math.min(CURRENT_YEAR + 2, y + 1))}
              disabled={displayYear >= CURRENT_YEAR + 2}
            >
              &rsaquo;
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
            {MONTHS.map((m) => {
              const isSelected = month === m.value && String(displayYear) === String(year);
              return (
                <button
                  key={m.value}
                  type="button"
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    padding: "6px 2px",
                    fontSize: "0.72rem",
                    fontWeight: isSelected ? 700 : 400,
                    background: isSelected ? "#343a40" : "white",
                    color: isSelected ? "white" : "#333",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    onChange(m.value, String(displayYear));
                    setIsOpen(false);
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * FormalizarModal: convierte una cotización externa en oportunidad interna.
 *
 * Props:
 *  - cotizacion: objeto de la tabla cotizaciones
 *  - clientes: array de clientes para el dropdown
 *  - intermediarioNombre: nombre de la agencia (derivado del creador)
 *  - tipoCotizacion: "agencia" | "cliente" (rol del creador)
 *  - pais: string
 *  - monedaSymbol: string
 *  - formatNum: function
 *  - onSubmit: async (cotizacion, formData) => void
 *  - onClose: () => void
 */
const FormalizarModal = ({
  cotizacion,
  clientes,
  intermediarioNombre,
  tipoCotizacion,
  pais,
  monedaSymbol,
  formatNum,
  onSubmit,
  onClose,
}) => {
  const now = new Date();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
  const currentYear = String(now.getFullYear());

  const [clienteId, setClienteId] = useState(cotizacion.cliente_id ? String(cotizacion.cliente_id) : "");
  const [campana, setCampana] = useState("");
  const [canal, setCanal] = useState("");
  const [estatus, setEstatus] = useState("");
  const [mesCierre, setMesCierre] = useState(currentMonth);
  const [anoCierre, setAnoCierre] = useState(currentYear);
  const [mesFactura, setMesFactura] = useState(currentMonth);
  const [anoFactura, setAnoFactura] = useState(currentYear);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (cotizacion.cliente_id) {
      setClienteId(String(cotizacion.cliente_id));
    }
  }, [cotizacion.cliente_id]);

  const validate = useCallback(() => {
    const errs = {};
    if (!clienteId) errs.cliente = "Selecciona un cliente";
    if (!canal) errs.canal = "Selecciona un canal";
    if (!estatus) errs.estatus = "Selecciona un estatus";

    // Validar cierre <= factura
    const cierreDate = new Date(`${anoCierre}-${mesCierre}-01`);
    const facturaDate = new Date(`${anoFactura}-${mesFactura}-01`);
    if (cierreDate > facturaDate) {
      errs.fechas = "El cierre debe ser igual o anterior a la factura";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [clienteId, canal, estatus, mesCierre, anoCierre, mesFactura, anoFactura]);

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      const selectedCliente = clientes.find((c) => String(c.id) === clienteId);

      await onSubmit(cotizacion, {
        cliente_id: parseInt(clienteId),
        cliente_nombre: selectedCliente?.nombre || cotizacion.cliente_nombre,
        campana: campana.trim().toUpperCase() || null,
        canal,
        estatus,
        mes_cierre: mesCierre,
        ano_cierre: anoCierre,
        mes_factura: mesFactura,
        ano_factura: anoFactura,
        intermediario_nombre: intermediarioNombre || null,
        tipo_cotizacion: tipoCotizacion || "agencia",
      });
    } catch (err) {
      console.error("Error formalizando:", err);
      toast.error(`Error al formalizar: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const inversionDisplay = cotizacion.moneda_dolar
    ? `USD ${formatNum(cotizacion.inversion_final_total)}`
    : `${monedaSymbol} ${formatNum(cotizacion.inversion_final_total)}`;

  const pantallas = (cotizacion.ascensores_seleccionados || 0) + (cotizacion.totems_seleccionados || 0);
  const tipoLabel = cotizacion.is_programmatic_selected ? "Programmatic" : "Digital";

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h5>Formalizar Propuesta — {cotizacion.codigo_generado}</h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        <div className={styles.modalBody}>
          {/* Datos de la cotización (read-only) */}
          <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f8f9fa", borderRadius: 6 }}>
            <div className={styles.readOnlyRow}>
              <span className={styles.label}>Inversión</span>
              <span className={styles.value}>{inversionDisplay}</span>
            </div>
            <div className={styles.readOnlyRow}>
              <span className={styles.label}>Edificios / Pantallas</span>
              <span className={styles.value}>
                {cotizacion.edificios_unicos_total || 0} / {pantallas}
              </span>
            </div>
            <div className={styles.readOnlyRow}>
              <span className={styles.label}>Tipo</span>
              <span className={styles.value}>{tipoLabel}</span>
            </div>
            <div className={styles.readOnlyRow}>
              <span className={styles.label}>Plazo</span>
              <span className={styles.value}>{cotizacion.plazo || 0} días</span>
            </div>
            {cotizacion.nombre_cotizacion && (
              <div className={styles.readOnlyRow}>
                <span className={styles.label}>Descripción</span>
                <span className={styles.value}>{cotizacion.nombre_cotizacion}</span>
              </div>
            )}
          </div>

          {/* Formulario */}
          <div className={styles.formGroup}>
            <label>Cliente Publicitario *</label>
            <select className="form-select" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">-- Seleccionar cliente --</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                  {c.empresa ? ` (${c.empresa})` : ""}
                  {c.producto ? ` - ${c.producto}` : ""}
                </option>
              ))}
            </select>
            {errors.cliente && <span className={styles.errorText}>{errors.cliente}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Campaña (opcional, máx 15 caracteres)</label>
            <input
              type="text"
              className="form-control"
              maxLength={15}
              value={campana}
              onChange={(e) => setCampana(e.target.value.replace(/\s+$/, ""))}
              placeholder="Ej: VERANO 2026"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Canal *</label>
              <select className="form-select" value={canal} onChange={(e) => setCanal(e.target.value)}>
                <option value="">Seleccionar</option>
                <option value="B2C">B2C</option>
                <option value="B2B">B2B</option>
                <option value="B2G">B2G</option>
              </select>
              {errors.canal && <span className={styles.errorText}>{errors.canal}</span>}
            </div>

            <div className={styles.formGroup}>
              <label>Estatus *</label>
              <select className="form-select" value={estatus} onChange={(e) => setEstatus(e.target.value)}>
                <option value="">Seleccionar</option>
                <option value="100%">100%</option>
                <option value="90%">90%</option>
                <option value="75%">75%</option>
                <option value="50%">50%</option>
              </select>
              {errors.estatus && <span className={styles.errorText}>{errors.estatus}</span>}
            </div>
          </div>

          <div className={styles.formRow}>
            <MonthYearPicker
              label="Cierre *"
              month={mesCierre}
              year={anoCierre}
              onChange={(m, y) => {
                setMesCierre(m);
                setAnoCierre(y);
              }}
            />
            <MonthYearPicker
              label="Facturación *"
              month={mesFactura}
              year={anoFactura}
              onChange={(m, y) => {
                setMesFactura(m);
                setAnoFactura(y);
              }}
            />
          </div>
          {errors.fechas && <span className={styles.errorText}>{errors.fechas}</span>}
        </div>

        <div className={styles.modalFooter}>
          <button className="btn btn-sm btn-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button className="btn btn-sm btn-success" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Formalizando..." : "Formalizar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormalizarModal;
